import { Package, ExternalLink, Hash } from 'lucide-react';
import { useItemStore } from '@/store';
import { cn } from '@/lib/utils';

type Props = {
  itemName: string;
  itemID: number;
};

function ItemColumn({ itemID, itemName }: Props) {
  const { openItemDialog } = useItemStore();

  const handleClick = () => {
    openItemDialog(itemID);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open details for ${itemName}`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
      className={cn(
        "group relative flex w-full min-w-[220px] cursor-pointer items-start gap-3 rounded-lg",
        "bg-gradient-to-r from-background to-muted/10",
        "border border-border/50 px-3 py-2",
        "transition-all duration-200",
        "hover:border-primary/50 hover:bg-gradient-to-r hover:from-primary/5 hover:to-primary/10",
        "hover:shadow-md dark:hover:shadow-primary/10",
        "focus:outline-none focus:ring-2 focus:ring-primary/30"
      )}
    >
      <div className="mt-0.5 rounded-md bg-primary/10 p-1.5">
        <Package className="h-3.5 w-3.5 text-primary" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className={cn(
            "line-clamp-1 text-sm font-medium",
            "text-foreground/90 group-hover:text-foreground",
            "transition-colors duration-200"
          )}>
            {itemName}
          </h3>
          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0" />
        </div>
        
        <div className="mt-0.5 flex items-center gap-1">
          <Hash className="h-3 w-3 text-muted-foreground/60" />
          <span className={cn(
            "font-mono text-xs",
            "text-muted-foreground/70 group-hover:text-primary/70",
            "transition-colors duration-200"
          )}>
            {itemID}
          </span>
        </div>
      </div>
      
      <div className={cn(
        "absolute inset-0 rounded-lg bg-gradient-to-r from-primary/10 to-primary/5",
        "opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
      )} />
    </div>
  );
}

export default ItemColumn;